/**
 * Most Popular and Trending colour rows — PDP.
 *
 * Both rows are built from one Storefront request for the product's own colourways
 * (custom.other_colours), so a swatch in either row always keeps you on the same item and only
 * changes its colour.
 *
 * Renders the best-selling colourways OF THIS PRODUCT.
 *
 * Previously this queried every product sharing the current product's `product_type`, paged
 * through up to 5,000 of them (5 sequential requests on a Value Set PDP), filtered client-side
 * to a matching `custom.product_category` and took the first five. It never looked at
 * `custom.other_colours`, so the row was really "the five best-selling colours in this category"
 * and was identical on every PDP in that category. On a set product that meant offering colours
 * the set does not come in — the swatch linked to a different holder/sleeve combination in that
 * colour, which reads as "not available in that colourway".
 *
 * It now asks for exactly the SKUs in `custom.other_colours` — the product's real colourways,
 * the same list the "all colours" grid is built from — in one request, sorted by best-selling.
 * Anything it can render is a genuine colourway by construction.
 */

(function () {
  'use strict';

  const STOREFRONT_TOKEN = '949d9e512aefae21c564449dfbfe09e5';
  const GRAPHQL_ENDPOINT =
    (window.theme && window.theme.routes && window.theme.routes.storefrontAPI_test) ||
    'https://' + window.Shopify.shop + '/api/2026-07/graphql.json';

  const QUERY = `
    query PopularColourways($query: String!) {
      products(first: 250, query: $query, sortKey: BEST_SELLING) {
        edges {
          node {
            handle
            title
            availableForSale
            tags
            metafield_colour: metafield(namespace: "custom", key: "Colour") { value }
            metafield_colour_family: metafield(namespace: "custom", key: "colour_family") { value }
          }
        }
      }
    }
  `;

  /**
   * The theme elsewhere builds this as "tag:" + "SKU_A OR SKU_B", which only tag-scopes the first
   * term and leaves the rest as free-text matches. Every term is scoped here.
   */
  function buildQuery(skus) {
    return skus.map((sku) => `tag:'SKU_${sku}'`).join(' OR ');
  }

  async function fetchColourways(skus) {
    const response = await fetch(GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify({ query: QUERY, variables: { query: buildQuery(skus) } }),
    });

    const data = await response.json();

    if (data.errors) {
      console.error('Most Popular colours — GraphQL errors:', data.errors);
      return [];
    }

    return data.data.products.edges.map((edge) => edge.node);
  }

  /**
   * Swatch images come from the `colour_swatches_list` metaobject, exposed by layout/theme.liquid
   * as #colour-swatches-list. That is the same source the "all colours" grid uses, so the two rows
   * stay visually consistent. Falls back to the CDN filename convention if it is unavailable.
   */
  function buildSwatchImageLookup() {
    const el = document.getElementById('colour-swatches-list');
    const lookup = {};

    if (!el || !el.value) return lookup;

    try {
      JSON.parse(el.value).forEach((row) => {
        if (row && row.colour_name && row.image) {
          lookup[row.colour_name.trim().toLowerCase()] = row.image;
        }
      });
    } catch (error) {
      console.warn('Most Popular colours — could not read #colour-swatches-list:', error);
    }

    return lookup;
  }

  function imageCandidates(colour, lookup) {
    const mapped = lookup[colour.trim().toLowerCase()];
    if (mapped) return [mapped];

    const base = '//' + window.Shopify.shop + '/cdn/shop/files/';
    return [
      base + colour.toLowerCase().replace(/\s+/g, '_') + '.png',
      base + colour.replace(/\s+/g, '_') + '.png',
      base + colour.toLowerCase().replace(/\s+/g, '-') + '.png',
    ];
  }

  function selectPopular(products, requestedSkus, currentHandle, limit) {
    const allowed = new Set(requestedSkus.map((sku) => `SKU_${sku}`));
    const seenColours = new Set();
    const popular = [];

    for (const product of products) {
      // Exact tag match. The previous substring test meant DUTHR54-CPT225-WH matched a query
      // containing SKU_CGS6.535-DUTHR54-CPT225-WH.
      if (!product.tags.some((tag) => allowed.has(tag))) continue;
      if (product.tags.includes('hidden')) continue;

      // The colour being viewed is shown rather than skipped, so the row always includes where
      // you are. It is exempt from the sold-out filter for the same reason — otherwise viewing a
      // sold-out colour would drop it from the row.
      const isCurrent = product.handle === currentHandle;

      // Offering a sold-out colourway as "Most Popular" is a dead end.
      if (!isCurrent && !product.availableForSale) continue;

      const colour = product.metafield_colour && product.metafield_colour.value;
      if (!colour || !colour.trim()) continue;

      const normalised = colour.trim().toLowerCase();
      if (seenColours.has(normalised)) continue;

      popular.push({
        handle: product.handle,
        url: '/products/' + product.handle,
        colour: colour,
        colourFamily: product.metafield_colour_family && product.metafield_colour_family.value,
        isCurrent: isCurrent,
      });
      seenColours.add(normalised);

      if (popular.length >= limit) break;
    }

    return popular;
  }

  /**
   * Trending = the collection's curated colour list, expressed as this product's colourways.
   * The curated entry supplies the name and swatch colour; the link is resolved to the colourway
   * of the current product that carries the same colour name. A curated colour the product isn't
   * made in has no colourway to link to, so it is dropped.
   */
  function selectTrending(products, curated, requestedSkus, currentHandle, limit) {
    const allowed = new Set(requestedSkus.map((sku) => `SKU_${sku}`));

    // colour name -> the colourway product carrying it
    const byColour = new Map();
    for (const product of products) {
      if (!product.tags.some((tag) => allowed.has(tag))) continue;
      if (product.tags.includes('hidden')) continue;
      const colour = product.metafield_colour && product.metafield_colour.value;
      if (!colour || !colour.trim()) continue;
      const key = colour.trim().toLowerCase();
      if (!byColour.has(key)) byColour.set(key, product);
    }

    const seen = new Set();
    const trending = [];

    for (const entry of curated) {
      if (!entry || !entry.name || !entry.colour) continue;

      const key = String(entry.name).trim().toLowerCase();
      if (seen.has(key)) continue;

      const match = byColour.get(key);
      if (!match) continue;

      const isCurrent = match.handle === currentHandle;

      // Sold-out colourways are skipped, except the colour being viewed.
      if (!isCurrent && !match.availableForSale) continue;

      trending.push({
        url: '/products/' + match.handle,
        name: entry.name,
        colour: entry.colour,
        colourFamily: match.metafield_colour_family && match.metafield_colour_family.value,
        isCurrent: isCurrent,
      });
      seen.add(key);

      if (trending.length >= limit) break;
    }

    return trending;
  }

  function renderTrending(items) {
    const container = document.getElementById('trending-colours-container');
    const grid = document.getElementById('trending-colours-grid');

    if (!container || !grid) return;

    if (!items.length) {
      container.style.display = 'none';
      return;
    }

    grid.innerHTML = '';

    items.forEach((item) => {
      const swatch = document.createElement('a');
      swatch.href = item.url;
      swatch.className = 'colour-swatch nd-colour-swatch' + (item.isCurrent ? ' aa-swatch--current' : '');
      swatch.style.background = item.colour;
      swatch.setAttribute('data-name', item.name);
      swatch.setAttribute('title', item.name);
      if (item.colourFamily) swatch.setAttribute('data-colour-family', item.colourFamily);
      grid.appendChild(swatch);
    });

    container.style.display = '';
  }

  function render(products) {
    const container = document.getElementById('popular-colours-container');
    const grid = document.getElementById('popular-colours-grid');

    if (!container || !grid) return;

    if (!products.length) {
      container.style.display = 'none';
      return;
    }

    const lookup = buildSwatchImageLookup();

    grid.innerHTML = '';

    products.forEach((product) => {
      const urls = imageCandidates(product.colour, lookup)
        .map((url) => `url('${url}')`)
        .join(', ');

      const swatch = document.createElement('a');
      swatch.href = product.url;
      swatch.className =
        'colour-swatch nd-colour-swatch product-swatch-button product-swatch-button--colour' +
        (product.isCurrent ? ' aa-swatch--current' : '');
      swatch.style.backgroundImage = urls;
      swatch.style.backgroundSize = 'cover';
      swatch.style.backgroundPosition = 'center';
      swatch.setAttribute('data-name', product.colour);
      swatch.setAttribute('title', product.colour);

      if (product.colourFamily) {
        swatch.setAttribute('data-colour-family', product.colourFamily);
      }

      grid.appendChild(swatch);
    });

    container.style.display = '';
  }

  /**
   * `other_colours` is a Liquid array by the time it reaches the template, so
   * `{{ other_colours | escape }}` renders a JSON array literal — ["SKU_A", "SKU_B"] — not the
   * pipe-delimited string the metafield holds. The "all colours" grid gets away with reading it
   * because jQuery's .data() parses JSON for you. Handle both forms explicitly.
   */
  function parseSkuList(raw) {
    const value = (raw || '').trim();
    if (!value) return [];

    if (value.startsWith('[')) {
      try {
        return JSON.parse(value).map((sku) => String(sku).trim()).filter(Boolean);
      } catch (error) {
        console.warn('Most Popular colours — could not parse data-request-products:', error);
        return [];
      }
    }

    return value.split('|').map((sku) => sku.trim()).filter(Boolean);
  }

  async function init() {
    const popularContainer = document.getElementById('popular-colours-container');
    const trendingContainer = document.getElementById('trending-colours-container');
    if (!popularContainer && !trendingContainer) return;

    // Both rows read the same colourway list; either container can supply it.
    const source = popularContainer || trendingContainer;
    const requestedSkus = parseSkuList(source.dataset.requestProducts);

    // A product with no other_colours has no colourways for either row.
    if (!requestedSkus.length) {
      if (popularContainer) popularContainer.style.display = 'none';
      if (trendingContainer) trendingContainer.style.display = 'none';
      return;
    }

    const currentHandle = source.dataset.productHandle;

    let curated = [];
    if (trendingContainer) {
      try {
        curated = JSON.parse(trendingContainer.dataset.trending || '[]');
      } catch (error) {
        console.warn('Trending colours — could not parse data-trending:', error);
      }
    }

    try {
      const products = await fetchColourways(requestedSkus);

      if (popularContainer) {
        const limit = parseInt(popularContainer.dataset.limit, 10) || 5;
        render(selectPopular(products, requestedSkus, currentHandle, limit));
      }

      if (trendingContainer) {
        const limit = parseInt(trendingContainer.dataset.limit, 10) || 5;
        renderTrending(selectTrending(products, curated, requestedSkus, currentHandle, limit));
      }
    } catch (error) {
      console.error('Colour rows — failed to load:', error);
      if (popularContainer) popularContainer.style.display = 'none';
      if (trendingContainer) trendingContainer.style.display = 'none';
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
